// Pure helpers for the report page (Talk BI spec §7.3, Tasks 4.4a-b): titles, the subtitle, the table a panel's
// rows make, CSV, the empty and error states, cross-filter picks and chips. No DOM: bi-report.jsx draws.
import {GRAINS} from './bi-tables.mjs';
import {qty} from './bi-format.mjs';
import {periodLabel, compareLabel, inclusiveEnd} from './data-dashboards.mjs';

const COUNT = '__count';
const GRAIN_WORDS = {year: ['שנה', 'Year'], quarter: ['רבעון', 'Quarter'], month: ['חודש', 'Month']};
export const nameOf = m => (typeof m === 'string' ? m : m.field);
// A field's catalog title; the field's own name when the catalog has not answered (or never will).
export function fieldTitle(dataset, name, lang) {
  const he = lang === 'he';
  if (name === COUNT) return he ? 'מספר רשומות' : 'Record count';
  if (GRAINS.includes(name)) return GRAIN_WORDS[name][he ? 0 : 1];
  return dataset?.fields?.find(f => f.name === name)?.title || name;
}
const measures = d => (d.kind === 'kpi' ? [d.measure] : d.kind === 'chart' ? d.y : d.kind === 'pivot' ? [d.value] : d.values);
const dims = d => (d.kind === 'kpi' ? [] : d.kind === 'chart' ? [d.x, d.series].filter(Boolean) : d.kind === 'pivot' ? [...d.rows, d.cols].filter(Boolean) : d.rows);
export const dateFieldOf = (definition, dataset) => definition.date_field || dataset?.default_date || null;
// A dimension value as a chart label: a blank reads as a dash.
export const labelOf = v => (v === null || v === undefined || v === '' ? '—' : String(v));

// unit · as of now · period · date field · to date · compare · filter (spec §7.3). The unit is the measure's
// title: the catalog has no units. Parts, not one string: each keeps its own direction (bdi in the frame).
export function visualSubtitle({definition, title, result, params, dataset, today, lang}) {
  const he = lang === 'he';
  const dateField = result ? result.date_field : dateFieldOf(definition, dataset);
  // no date field at all: the panel reads what is there now (a catalog not read yet says nothing)
  const now = result ? !result.date_field : !!dataset && !dateField;
  const dated = !!params.period && !!dateField && (!result || !!result.period);
  const filters = [
    // a panel's own cross_filter does not filter it (app/bi/run.py): it marks the pick instead
    ...Object.entries(params.choices).filter(([name, c]) => c.dataset === definition.dataset && c.value !== null && name !== definition.cross_filter)
      .map(([, c]) => [c.field, c.value]),
    ...Object.entries(definition.filters || {}),
  ];
  const unit = measures(definition).map(m => fieldTitle(dataset, nameOf(m), lang)).join(', ');
  return [
    unit !== title && unit,
    now && (he ? 'נכון לעכשיו' : 'As of now'),
    dated && periodLabel(params.period, today, lang),
    dated && (he ? `לפי ${fieldTitle(dataset, dateField, lang)}` : `by ${fieldTitle(dataset, dateField, lang)}`),
    dated && result?.compare_clipped && (he ? 'עד היום' : 'to date'),
    dated && (params.compare || definition.compare) && compareLabel(params.period, today, lang),
    ...filters.map(([field, value]) => `${fieldTitle(dataset, field, lang)}: ${value}`),
  ].filter(Boolean);
}

// The reply's rows as a table: dims then measures under their titles, cells only (never a pivot's total
// rows), without the runner's __n/__g bookkeeping. keep: the result columns kept, in order.
export function tableOf(definition, result, dataset, lang) {
  const flags = result.columns.flatMap((c, i) => (/^__g\d+$/.test(c) ? [i] : []));
  const cells = flags.length ? result.rows.filter(r => flags.every(i => Number(r[i]) === 0)) : result.rows;
  const keep = result.columns.flatMap((c, i) => (c.startsWith('__') ? [] : [i]));
  const names = [...dims(definition), ...measures(definition).map(nameOf)];
  return {keep, titles: keep.map((i, k) => fieldTitle(dataset, names[k] ?? result.columns[i], lang)), rows: cells.map(r => keep.map(i => r[i]))};
}

const NUMBER = /^[+-]?\d+(\.\d+)?$/;
// Text a spreadsheet would run as a formula gets a leading quote; a number (the runner's decimal text) never does.
const csvCell = v => {
  const t = v === null || v === undefined ? '' : String(v);
  return `"${(!NUMBER.test(t) && /^[\s\u0000-\u001f\u007f]*[=+\-@]/u.test(t) ? `'${t}` : t).replace(/"/g, '""')}"`;
};
// The panel's table as CSV: a BOM for Excel, titles as headers, values as the server sent them.
export function csvOf(definition, result, dataset, lang) {
  const t = tableOf(definition, result, dataset, lang);
  return `\uFEFF${[t.titles, ...t.rows].map(r => r.map(csvCell).join(',')).join('\r\n')}`;
}

const day = (iso, lang) => new Intl.DateTimeFormat(lang === 'he' ? 'he-IL' : 'en-GB',
  {day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC'}).format(new Date(`${String(iso).slice(0, 10)}T00:00:00Z`));
// The empty state in the page's language, from the period read and the date field's title (the server's
// empty_reason is English and stays a secondary line).
export function emptyText(result, dateTitle, lang) {
  const he = lang === 'he', p = result.period;
  if (!p) return he ? 'אין נתונים להצגה.' : 'No rows to show.';
  const a = day(p.start, lang), b = day(inclusiveEnd({end_exclusive: String(p.end_exclusive).slice(0, 10)}), lang);
  const by = dateTitle ? (he ? ` לפי ${dateTitle}` : ` by ${dateTitle}`) : '';
  return he ? `אין נתונים בין ${a} ל־${b}${by}.` : `No rows between ${a} and ${b}${by}.`;
}

const STATUS = {404: 'unavailable', 422: 'query_refused', 503: 'query_failed'};
const KNOWN = ['unavailable', 'query_refused', 'query_failed', 'invalid_result'];
// A definition panel's failure ({status, category, detail}: a refresh-board entry's error, or a per-panel HTTP
// error without a category) -> {state, text, note}; null when the generic message fits (429, 5xx, network).
export function panelError({status, category, detail} = {}, lang) {
  const he = lang === 'he', w = (a, b) => (he ? a : b);
  const kind = KNOWN.includes(category) ? category : detail === 'panel result unavailable' ? 'invalid_result' : STATUS[status];
  const note = typeof detail === 'string' ? detail : null;
  if (kind === 'unavailable') return {state: 'unavailable', text: w('מקור הנתונים של הפריט אינו זמין.', 'This panel’s dataset is unavailable.'), note};
  if (kind === 'query_failed') return {state: 'failed', text: w('הקריאה נכשלה או ארכה יותר מדי. רעננו את הפריט כדי לנסות שוב.',
    'The read failed or took too long. Refresh the panel to try again.'), note};
  if (kind === 'invalid_result') return {state: 'failed', text: w('התוצאה של הפריט אינה זמינה.', 'This panel’s result is unavailable.'), note};
  if (kind !== 'query_refused') return null;
  if (detail?.code === 'query_refused') {
    const cost = qty(Math.round(detail.cost)), ceiling = qty(Math.round(detail.ceiling));
    return {state: 'refused', note: detail.message ?? null, text: w(
      `השאילתה כבדה מדי לקריאה מהלוח: עלות משוערת ${cost}, מעל התקרה ${ceiling}. צמצמו את התקופה או הוסיפו מסנן.`,
      `This query is too heavy to read from the board: estimated cost ${cost}, over the ceiling of ${ceiling}. Narrow the period or add a filter.`)};
  }
  if (detail?.reason) return {state: 'refused', text: w('הגדרת הפריט כבר אינה מתאימה למקור הנתונים שלו.', 'This panel’s definition no longer fits its dataset.'),
    note: `${detail.field_path}: ${detail.reason}`};
  return {state: 'refused', text: w('השרת סירב לקרוא את הפריט.', 'The server refused to read this panel.'), note};
}

// Cross-filter (spec §5.2): a choice's value set or cleared; the params given are left as they were.
export const withChoice = (params, name, value) => ({...params, choices: {...params.choices, [name]: {...params.choices[name], value}}});
export const chipsOf = (params, datasets, lang) => Object.entries(params.choices).filter(([, c]) => c.value !== null)
  .map(([name, c]) => ({name, label: `${fieldTitle(datasets[c.dataset], c.field, lang)}: ${c.value}`}));
// The choice a click on this panel sets: its cross_filter, when that choice is on the panel's dataset and
// filters the dimension clicked (a donut's x, a pivot row dim at index `at`). The server checks the first only.
export function pickOf(definition, params) {
  const name = definition.cross_filter, c = name ? params.choices[name] : null;
  if (!c || c.dataset !== definition.dataset) return null;
  const at = definition.kind === 'pivot' ? definition.rows.indexOf(c.field)
    : definition.kind === 'chart' && definition.mark === 'donut' && definition.x === c.field ? 0 : -1;
  return at < 0 ? null : {name, at, value: c.value};
}

// The choice value a click sets: a blank slice or row clears the choice (a blank cannot be filtered on).
export const pickedValue = v => (v === null || v === undefined || v === '' ? null : String(v));

// Colour follows the entity (spec §7.1): the order a panel first drew its entities in, kept, so a re-ranked
// reply does not swap their colours; an entity not seen before takes the next slot.
export function stableOrder(kept, keys) {
  if (!kept) return [...keys];
  const added = keys.filter(k => !kept.includes(k));
  return added.length ? [...kept, ...added] : kept;
}

// The previous period's rows keyed by this period's labels: a date moves a year forward, a category is itself.
export function compareMap(rows) {
  if (!rows) return null;
  const key = v => (typeof v === 'string' && /^\d{4}-\d\d-\d\d/.test(v) ? `${String(Number(v.slice(0, 4)) + 1).padStart(4, '0')}${v.slice(4)}` : labelOf(v));
  return new Map(rows.map(r => [key(r[0]), r[1]]));
}

export const shownOf = (shown, of, lang) => (lang === 'he' ? `מוצגות ${qty(shown)} מתוך ${qty(of)} שורות` : `Showing ${qty(shown)} of ${qty(of)} rows`);
